import React, { useCallback, useState, useEffect, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { trackButtonClick } from '../../utils/gtm';

const QUALIFICATION_ROLES = [
  { label: "CTO", value: "CTO", icon: "🏗️" },
  { label: "CEO", value: "CEO", icon: "🎯" },
  { label: "Developer", value: "Developer", icon: "💻" },
  { label: "QA Lead", value: "QA Lead", icon: "🔍" },
  { label: "Engineering Manager", value: "Engineering Manager", icon: "📊" },
  { label: "Other", value: "Other", icon: "👤" },
];

const NAV_ITEMS = [
  { name: "Features", href: "#features" },
  { name: "Pricing", href: "#pricing" },
  { name: "Setup", href: "/setup" },
  { name: "Contact", href: "/contact" },
  { name: "What We Do", href: "/what-we-do" },
  { name: "Blog", href: "/blog" },
];

const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showQualification, setShowQualification] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const isMobile = useMediaQuery('(max-width:900px)', { noSsr: true });
  const navigate = useNavigate();
  const currentLocation = useLocation();

  const handleDrawerToggle = useCallback(() => setMobileOpen((prev) => !prev), []);

  const handleSignIn = useCallback(() => {
    trackButtonClick('sign_in', 'navigation_header_landing_page');
    navigate('/login');
  }, [navigate]);
  
  const handleSignUp = useCallback(() => {
    trackButtonClick('sign_up', 'navigation_header_landing_page');
    navigate('/signup');
  }, [navigate]);

  const handleBookDemo = useCallback(() => {
    trackButtonClick('book_sniffr_demo', 'navigation_header_landing_page');
    setShowQualification(true);
  }, []);

  const handleRoleSelect = useCallback((role) => {
    setSelectedRole(role);
    trackButtonClick('qualification_role_selected', 'navigation_header_landing_page', { role });
    if (window.fbq) window.fbq('track', 'Lead', { content_name: 'book_sniffr_demo', content_category: role });
    setShowQualification(false);
    setShowCalendly(true);
  }, []);

  const handleCloseQualification = useCallback(() => {
    setShowQualification(false);
  }, []);

  const handleCloseCalendly = useCallback(() => {
    setShowCalendly(false);
    setSelectedRole('');
  }, []);

  const calendlyUrl = selectedRole
    ? `https://calendly.com/sniffr-sales/30min?a1=${encodeURIComponent(selectedRole)}`
    : 'https://calendly.com/sniffr-sales/30min';
  
  const handleContact = useCallback(() => {
    trackButtonClick('contact', 'navigation_header_landing_page');
    navigate('/contact');
  }, [navigate]);
  
  const handleSetup = useCallback(() => {
    trackButtonClick('setup', 'navigation_header_landing_page');
    navigate('/setup');
  }, [navigate]);

  const handleWhatWeDo = useCallback(() => {
    trackButtonClick('what_we_do', 'navigation_header_landing_page');
    navigate('/what-we-do');
  }, [navigate]);

  const handleBlog = useCallback(() => {
    trackButtonClick('blog', 'navigation_header_landing_page');
    navigate('/blog');
  }, [navigate]);

  const scrollToSection = useCallback((hash) => {
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleNavigationClick = useCallback((item, gtmLocation) => {
    trackButtonClick(`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`, gtmLocation);
    
    if (item.name === 'Setup') {
      handleSetup();
    } else if (item.name === 'Contact') {
      handleContact();
    } else if (item.name === 'What We Do') {
      handleWhatWeDo();
    } else if (item.name === 'Blog') {
      handleBlog();
    } else if (item.href.startsWith('#')) {
      if (currentLocation.pathname === '/') {
        // Already on home page — scroll directly
        scrollToSection(item.href);
      } else {
        // On another page — navigate home and pass the hash to scroll to
        navigate('/', { state: { scrollTo: item.href } });
      }
    }
  }, [handleContact, handleSetup, handleWhatWeDo, handleBlog, currentLocation.pathname, navigate, scrollToSection]);

  useEffect(() => {
    if (showCalendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [showCalendly]);

  return (
    <>
      {/* Sticky Navigation */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          width: "100%",
          fontFamily: "Segoe UI, monospace",
        }}
      >
        <Box
          sx={{
            bgcolor: "#f4f4f4",
            boxShadow: "none",
            border: "0.5px solid #5998EF",
            borderRadius: '20px',
            mx: "auto",
            px: { xs: 2, sm: 3, md: 3, lg: 5 },
            maxWidth: { xs: "96%", sm: "94%", md: "92%", lg: "90%" },
            fontFamily: "Segoe UI, monospace",
            overflow: "hidden"
          }}
        >
          <Toolbar sx={{
            justifyContent: "space-between",
            py: 1,
            px: 0,
            minHeight: { xs: 48, md: 55 },
            fontFamily: "Segoe UI, monospace"
          }}>
            {/* Logo Section */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, cursor: 'pointer', flexShrink: 0 }}
              onClick={() => navigate('/')}
            >
              <picture style={{ display: 'block', lineHeight: 0 }}>
                <source srcSet="/assets/logo.avif" type="image/avif" />
                <source srcSet="/assets/logo.webp" type="image/webp" />
                <Box component="img" src="/assets/logo.png" alt="Sniffr.ai Logo"
                  sx={{ height: { xs: 28, sm: 32, md: 34, lg: 40 }, display: 'block', borderRadius: '50%', transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.05)' } }}
                />
              </picture>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.15rem", lg: "1.5rem" },
                  background: "linear-gradient(90deg, #FF8052 0%, #00BFFF 50%, #B366FF 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  ml: { xs: 0.5, sm: 1 },
                  fontFamily: "Segoe UI, monospace",
                  whiteSpace: "nowrap",
                }}
              >
                Sniffr.ai
              </Typography>
            </Box>

            {/* Navigation Links — hidden on mobile */}
            {!isMobile && (
              <Box sx={{
                display: "flex",
                gap: { md: 0.5, lg: 1 },
                mx: { md: 3, lg: 4 },
                flex: 1,
                justifyContent: "center",
                flexWrap: "nowrap",
                overflow: "hidden",
              }}>
                {NAV_ITEMS.map((item) => (
                  <Button
                    key={item.name}
                    component="a"
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationClick(item, 'navigation_header_landing_page');
                    }}
                    sx={{
                      color: "#374151",
                      textTransform: "none",
                      fontSize: { md: "0.875rem", lg: "0.95rem", xl: "1rem" },
                      fontWeight: 500,
                      px: { md: 2.5, lg: 3 },
                      py: 1,
                      borderRadius: 1,
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      "&:hover": {
                        color: "#1f2937",
                        bgcolor: "#f9fafb"
                      },
                      transition: "all 0.2s ease",
                      fontFamily: "Segoe UI, monospace"
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>
            )}

            {/* CTA Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5, lg: 2 }, flexShrink: 0 }}>
              {/* Book Demo — shown only at lg+ to avoid overflow at md */}
              {!isMobile && (
                <Button
                  onClick={handleBookDemo}
                  sx={{
                    display: { md: "none", lg: "inline-flex" },
                    background: "linear-gradient(135deg, #38BDF8 0%, #3B82F6 100%)",
                    color: "white",
                    textTransform: "none",
                    fontSize: { lg: "0.95rem", xl: "1rem" },
                    px: { lg: 2.5, xl: 3 },
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 600,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: "linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.35)"
                    },
                    transition: "all 0.2s ease",
                    fontFamily: "Segoe UI, monospace"
                  }}
                >
                  Book Sniffr Demo
                </Button>
              )}
              {!isMobile && (
                <Button
                  onClick={handleSignIn}
                  sx={{
                    bgcolor: "#ffffff",
                    border: "1px solid #1f2937",
                    color: "#374151",
                    textTransform: "none",
                    fontSize: { md: "0.875rem", lg: "0.95rem", xl: "1rem" },
                    fontWeight: 600,
                    px: { md: 2.5, lg: 3 },
                    py: 1,
                    borderRadius: 1,
                    whiteSpace: "nowrap",
                    "&:hover": { color: "#1f2937", bgcolor: "#f9fafb" },
                    transition: "all 0.2s ease",
                    fontFamily: "Segoe UI, monospace"
                  }}
                >
                  Sign In
                </Button>
              )}
              {!isMobile && (
                <Button
                  variant="contained"
                  onClick={handleSignUp}
                  sx={{
                    bgcolor: "#000000",
                    color: "white",
                    textTransform: "none",
                    fontSize: { md: "0.875rem", lg: "0.95rem", xl: "1rem" },
                    px: { md: 2.5, lg: 3 },
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 600,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: "#1a1a1a",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)"
                    },
                    transition: "all 0.2s ease",
                    fontFamily: "Segoe UI, monospace"
                  }}
                >
                  Sign Up
                </Button>
              )}
              {isMobile && (
                <IconButton
                  aria-label="Open navigation menu"
                  aria-controls="mobile-nav-drawer"
                  aria-expanded={mobileOpen ? 'true' : 'false'}
                  aria-haspopup="true"
                  onClick={() => {
                    trackButtonClick('mobile_menu_toggle', 'navigation_header_landing_page');
                    handleDrawerToggle();
                  }}
                  sx={{
                    color: "#374151",
                    p: { xs: 1, sm: 1.25 },
                  }}
                >
                  <MenuIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        id="mobile-nav-drawer"
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ "& .MuiDrawer-paper": { bgcolor: "white", width: 280, fontFamily: "Segoe UI, monospace" } }}
      >
        <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ color: "#1f2937", fontWeight: 700 }}>
            Sniffr.ai
          </Typography>
        </Box>
        <List sx={{ pt: 2 }}>
          {NAV_ITEMS.map((item) => (
            <ListItemButton
              key={item.name}
              onClick={() => {
                handleNavigationClick(item, 'navigation_mobile_menu_landing_page');
                setMobileOpen(false);
              }}
              sx={{
                color: "#374151",
                mx: 1,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "#f9fafb"
                },
                fontFamily: "Segoe UI, monospace"
              }}
            >
              <ListItemText
                primary={item.name}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: 500,
                    fontSize: "1rem",
                    fontFamily: "Segoe UI, monospace"
                  }
                }}
              />
            </ListItemButton>
          ))}
          <Divider sx={{ bgcolor: "#e5e7eb", my: 2, mx: 2 }} />
          <ListItemButton
            onClick={() => { 
              trackButtonClick('sign_in', 'navigation_mobile_menu_landing_page');
              handleSignIn(); 
              setMobileOpen(false); 
            }}
            sx={{
              color: "#374151",
              mx: 1,
              borderRadius: 1,
              "&:hover": { bgcolor: "#f9fafb" }
            }}
          >
            <ListItemText
              primary="Sign In"
              sx={{ "& .MuiListItemText-primary": { fontWeight: 500, fontSize: "1rem" } }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => { 
              trackButtonClick('book_sniffr_demo', 'navigation_mobile_menu_landing_page');
              setMobileOpen(false);
              handleBookDemo();
            }}
            sx={{
              background: "linear-gradient(135deg, #38BDF8 0%, #3B82F6 100%)",
              color: "white",
              mx: 1,
              mt: 1,
              borderRadius: 1,
              "&:hover": { background: "linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)" },
              fontFamily: "Segoe UI, monospace"
            }}
          >
            <ListItemText
              primary="Book Sniffr Demo"
              sx={{ "& .MuiListItemText-primary": { fontWeight: 600, fontSize: "1rem", fontFamily: "Segoe UI, monospace" } }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => { 
              trackButtonClick('sign_up', 'navigation_mobile_menu_landing_page');
              handleSignUp(); 
              setMobileOpen(false); 
            }}
            sx={{
              bgcolor: "#1f2937",
              color: "white",
              mx: 1,
              mt: 1,
              borderRadius: 1,
              "&:hover": { bgcolor: "#111827" },
              fontFamily: "Segoe UI, monospace"
            }}
          >
            <ListItemText
              primary="Sign Up"
              sx={{ "& .MuiListItemText-primary": { fontWeight: 600, fontSize: "1rem", fontFamily: "Segoe UI, monospace" } }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Qualification Dialog */}
      <Dialog
        open={showQualification}
        onClose={handleCloseQualification}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#141414', fontFamily: 'Segoe UI, monospace' }}>
                One quick question 👋
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5, fontFamily: 'Segoe UI, monospace' }}>
                This helps us tailor the Sniffr demo to your role
              </Typography>
            </Box>
            <IconButton onClick={handleCloseQualification} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 2, color: '#374151', fontFamily: 'Segoe UI, monospace' }}>
            What best describes your role?
          </Typography>
          <Grid container spacing={1.5}>
            {QUALIFICATION_ROLES.map((role) => (
              <Grid item xs={12} sm={6} key={role.value}>
                <Button
                  onClick={() => handleRoleSelect(role.value)}
                  aria-label={`Select role: ${role.label}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                    textTransform: 'none',
                    gap: 1.5,
                    p: 1.75,
                    border: '1.5px solid #e5e7eb',
                    borderRadius: 2,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#5998EF',
                      bgcolor: '#f0f7ff',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>{role.icon}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#141414', fontFamily: 'Segoe UI, monospace' }}>
                    {role.label}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Calendly Demo Modal */}
      <Dialog
        open={showCalendly}
        onClose={handleCloseCalendly}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <IconButton
          onClick={handleCloseCalendly}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
        <div
          className="calendly-inline-widget"
          data-url={calendlyUrl}
          style={{ minWidth: '320px', height: '700px' }}
        />
      </Dialog>
    </>
  );
};

export default memo(Navigation);